import { MapperSchemaCreateRequest, Policy } from "@bluefalcon/clients-management-client-fetch";
import { Box, VStack, Grid, FormControl, Input, IconButton, Flex, Button } from "@chakra-ui/react";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState, useRef, ChangeEvent } from "react";
import { Dropdown } from "../../components/dropdown/dropdown.comp";
import { toUpperSnakeCase } from "../../utils/string.utils";
import { toast } from "../../utils/toast.utils";

type PolicyConnectionProps = {
    onSave: (policyMap: MapperSchemaCreateRequest[]) => void;
    policies: Policy[];
    policyMap: MapperSchemaCreateRequest[],
};

export const PolicyConnection = ({
    onSave,
    policies,
    policyMap,
}: PolicyConnectionProps) => {
    const [pairs, setPairs] = useState(policyMap);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const convertMapToRecord = (): Record<string, string> => {
        const record: Record<string, string> = {};
        pairs.forEach((col) => {
            record[col.key] = col.value;
        });
        return record;
    };

    const handleDownloadAsJSON = () => {
        const record = convertMapToRecord();
        const jsonString = JSON.stringify(record);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'data.json';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleButtonClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            try {
                const text = await file.text();
                const json: Record<string, string | number> = JSON.parse(text);
                const toBeSet: { key: string, value: string, type: 'policies' }[] = [];


                for (const key in json) {
                    if (typeof json[key] === 'number') {
                        toBeSet.push({
                            value: String(json[key]),
                            key,
                            type: 'policies',
                        });
                    } else if (typeof json[key] === 'string') {
                        const policy = policies.find((p) => String(p.id) === json[key]) ?? policies.find((p) => p.name === json[key]);
                        if (policy) {
                            toBeSet.push({
                                value: String(policy.id),
                                key,
                                type: 'policies',
                            });
                        } else {
                            console.log('missed policy for', json[key])
                            console.log(policies)
                        }
                    }
                }
                setPairs(toBeSet);
            } catch (error) {
                console.error(error);
            }
        }
    };

    const handleAddPair = () => {
        setPairs([...pairs, { key: '', value: '', type: 'policies' }]);
    };

    const handleRemovePair = (index: number) => {
        const updatedPairs = pairs.filter((_, i) => i !== index);
        setPairs(updatedPairs);
    };

    const handleChange = (index: number, field: string, value?: number | string) => {
        const updatedPairs = pairs.map((pair, i) =>
            i === index ? { ...pair, [field]: value } : pair
        );
        setPairs(updatedPairs);
    };

    const handleSave = () => {
        const valid = pairs
            .filter(({ key, value }) => !!(key.trim() && value))
            .map(({ key, value, type }) => ({ key: toUpperSnakeCase(key), value, type }));
        setPairs(valid);
        onSave(valid);
        toast('Saved');
    };

    return <Box
        p={4}
        mt={4}
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
        boxShadow="md"
        bg="white"
        w="100%"
        mx="auto"
    >
        <VStack spacing={6} align="stretch">
            {pairs.map((pair, index) => (
                <Grid templateColumns="repeat(3, 1fr)" gap={4} alignItems="center" key={index}>
                    <FormControl>
                        <Input
                            w={'300px'}
                            placeholder="Name on Sheet"
                            value={pair.key}
                            onChange={(e) => handleChange(index, 'key', e.target.value)}
                        />
                    </FormControl>

                    <FormControl>
                        <Dropdown
                            items={policies.map((p) => p.name)}
                            placeholder={policies.length ? "Policy" : 'Select Insurance/Coverage to see Policies'}
                            value={policies.find((p) => p.id === Number(pair.value))?.name}
                            onChange={(name) => handleChange(index, 'value', String(policies.find((p) => p.name === name)?.id))}
                        />
                    </FormControl>

                    <IconButton
                        ml={'auto'}
                        w={'25%'}
                        aria-label="Remove pair"
                        icon={<FontAwesomeIcon icon={faTrash} />}
                        onClick={() => handleRemovePair(index)}
                        colorScheme="red"
                        variant="outline"
                        size="sm"
                    />
                </Grid>
            ))}

            <Flex justify="flex-end" w="100%">
                <Flex mr={'auto'}>
                    <Button onClick={handleButtonClick} size={'sm'} variant={'solid'} colorScheme="warn">JSON</Button>
                    <input
                        type="file"
                        ref={fileInputRef}
                        accept=".json"
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />
                    <Button onClick={handleDownloadAsJSON} ml={2} size={'sm'} variant={'solid'} colorScheme="info">Download JSON</Button>
                </Flex>
                <Button onClick={handleAddPair} mr={2} size="sm" colorScheme="primary">
                    Add Policy Connection
                </Button>
                <Button colorScheme="teal" onClick={handleSave} size="sm">
                    Save
                </Button>
            </Flex>
        </VStack>
    </Box>

};